import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';

export abstract class HubService {
  public readonly hubConnection: HubConnection;

  constructor(url: string) {
    this.hubConnection = new HubConnectionBuilder()
      .withUrl(url)
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    this.hubConnection
      .start()
      .then()
      .catch((err: unknown) => {
        console.error("Hub couldn't start:", err);
      });
  }

  /**
   * Ensures the SignalR connection is ready before invoking server methods.
   * Handles all connection states appropriately.
   * @param timeoutMs Maximum time to wait for connection (default: 10000ms)
   * @throws Error if connection cannot be established or times out
   */
  public async ensureConnected(timeoutMs = 10000): Promise<void> {
    if (this.hubConnection.state === HubConnectionState.Connected) {
      // Already connected, proceed
      return;
    }

    if (this.hubConnection.state === HubConnectionState.Connecting) {
      // Already connecting, wait for it to complete
      await this.waitForConnection(timeoutMs);
      return;
    }

    if (this.hubConnection.state === HubConnectionState.Disconnected) {
      // Not started yet, start it now
      try {
        await this.hubConnection.start();
      } catch (error) {
        console.error('Failed to connect to SignalR hub:', error);
        throw new Error('Cannot establish SignalR connection - connection failed');
      }
      return;
    }

    throw new Error(`Cannot connect - connection is in ${this.hubConnection.state} state`);
  }

  private async waitForConnection(timeoutMs: number): Promise<void> {
    const startTime = Date.now();

    while (this.hubConnection.state === HubConnectionState.Connecting) {
      if (Date.now() - startTime > timeoutMs) {
        throw new Error('SignalR connection timeout');
      }
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    if (this.hubConnection.state !== HubConnectionState.Connected) {
      throw new Error(`Connection failed - state is ${this.hubConnection.state}`);
    }
  }
}
